import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supervisorApi } from "@/lib/SupervisorApi";
import { User, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SupervisorLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // 2FA challenge state
  const [challengeToken, setChallengeToken] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  const persistAndGo = (data) => {
    localStorage.setItem("supervisor_token", data.token);
    localStorage.setItem("supervisor_user", JSON.stringify(data.supervisor));
    toast.success("Καλώς ήρθατε, " + data.supervisor.username);
    navigate("/supervisor", { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!username || !password) return;
    setBusy(true);
    try {
      const r = await supervisorApi.login(username, password);
      if (r.data.requires_2fa) {
        setChallengeToken(r.data.challenge_token);
        toast.info("Απαιτείται κωδικός Google Authenticator");
      } else {
        persistAndGo(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία σύνδεσης");
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(totpCode)) {
      toast.error("Ο κωδικός πρέπει να είναι 6 ψηφία");
      return;
    }
    setBusy(true);
    try {
      const r = await supervisorApi.verify2fa(challengeToken, totpCode);
      persistAndGo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Λάθος κωδικός 2FA");
      setTotpCode("");
    } finally {
      setBusy(false);
    }
  };

  const cancel2fa = () => {
    setChallengeToken(null);
    setTotpCode("");
    setPassword("");
  };

  const show2fa = !!challengeToken;

  return (
    <div className="h-screen w-screen grid place-items-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-black">
      <div className="absolute top-6 right-8 text-zinc-700 text-xs font-mono tracking-wider uppercase">
        v1.0 — Supervisor Console
      </div>
      <form
        onSubmit={show2fa ? submit2fa : submit}
        className="w-[420px] rounded-2xl bg-zinc-900/90 backdrop-blur border border-zinc-800 shadow-2xl p-8"
      >
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="w-24 h-24 rounded-xl bg-white/95 grid place-items-center shadow-lg overflow-hidden">
            <img src="/ponte-pos-logo.jpg" alt="Ponte Pos" className="w-24 h-24 object-contain" />
          </div>
          <div className="text-center">
            <div className="font-heading font-bold text-2xl text-white leading-none">
              {show2fa ? "Έλεγχος 2FA" : "Supervisor Panel"}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2 tracking-wider uppercase font-bold">
              {show2fa ? "Google Authenticator κωδικός" : "Ponte Pos — Master Console"}
            </div>
          </div>
        </div>

        {!show2fa && (
          <>
            <label className="block mb-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 flex items-center gap-1.5">
                <User size={12} /> Username
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="supervisor-username"
                autoComplete="username"
                className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-base outline-none focus:border-amber-500"
                required
                disabled={busy}
              />
            </label>

            <label className="block mb-6">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 flex items-center gap-1.5">
                <Lock size={12} /> Password
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="supervisor-password"
                autoComplete="current-password"
                className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-base outline-none focus:border-amber-500"
                required
                disabled={busy}
              />
            </label>

            <button
              type="submit"
              disabled={busy || !username || !password}
              data-testid="supervisor-submit"
              className="w-full h-12 rounded-lg bg-gradient-to-r from-amber-500 to-rose-600 hover:from-amber-400 hover:to-rose-500 text-white font-bold text-base disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {busy ? "Σύνδεση..." : "Είσοδος"}
            </button>
          </>
        )}

        {show2fa && (
          <>
            <div className="mb-5 rounded-lg bg-amber-950/30 border border-amber-900/50 p-3 flex gap-2">
              <ShieldCheck size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-200">
                Άνοιξε την εφαρμογή <b>Google Authenticator</b> και δώσε τον 6-ψήφιο κωδικό που εμφανίζεται για το <b>{TOTP_ISSUER_LABEL}</b>.
              </div>
            </div>
            <label className="block mb-6">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 flex items-center gap-1.5">
                <ShieldCheck size={12} /> 6-ψήφιος κωδικός
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="supervisor-totp"
                autoComplete="one-time-code"
                className="w-full h-14 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-3xl font-mono tracking-[0.5em] text-center outline-none focus:border-amber-500"
                autoFocus
                required
                disabled={busy}
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancel2fa}
                disabled={busy}
                data-testid="supervisor-2fa-cancel"
                className="h-12 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-sm"
              >
                Άκυρο
              </button>
              <button
                type="submit"
                disabled={busy || totpCode.length !== 6}
                data-testid="supervisor-2fa-submit"
                className="flex-1 h-12 rounded-lg bg-gradient-to-r from-amber-500 to-rose-600 hover:from-amber-400 hover:to-rose-500 text-white font-bold text-base disabled:opacity-40 transition-all"
              >
                {busy ? "Επαλήθευση..." : "Επιβεβαίωση"}
              </button>
            </div>
          </>
        )}

        <div className="mt-6 text-center text-[11px] text-zinc-600">
          Restricted Access — Authorized Personnel Only
        </div>
      </form>
    </div>
  );
}

const TOTP_ISSUER_LABEL = "Ponte Pos Supervisor";
